import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';
import DisplayDate from '../../components/DisplayDate';
import DisplayDateExpiry from '../../components/DisplayDateExpiry';
import NoInfoMessage from '../../components/NoInfoMessage';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import AcceptModal from './AcceptModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';
import { ReactComponent as SwitchIcon } from '../../assets/svg/switch.svg';
import { ReactComponent as DeviceIcon } from '../../assets/svg/device.svg';

// TYPES
import { PolicyAdminDashboardPolicy, PolicyUserDashboardAcknowledgement } from '../../api/models';

const Policies: React.FC = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Check if User is viewing Policy as an Admin or not
	const viewPoliciesAsAdmin = useAppSelector((state) => state.user.viewPoliciesAsAdmin);

	// State to control Modals
	const [showAcceptModal, setShowAcceptModal] = React.useState<boolean>(false);

	const [policyId, setPolicyId] = React.useState<string>('');
	const [policyTitle, setPolicyTitle] = React.useState<string>('');

	// Set up Admin Data state and initial Admin data
	const [policiesAdminData, setPoliciesAdminData] = React.useState<PolicyAdminDashboardPolicy[]>([]);

	const getPoliciesAdminData = async () => {
		setIsLoading(true);

		const response = await apibridge.getPoliciesAdminData();

		response.result = response.result || [];

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setPoliciesAdminData(response.result);
		}
	};

	// Set up User Data state and initial User data
	const [policiesUserData, setPoliciesUserData] = React.useState<PolicyUserDashboardAcknowledgement[]>([]);

	const getPoliciesUserData = async () => {
		setIsLoading(true);

		const response = await apibridge.getPoliciesUserData();

		response.result = response.result || [];

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setPoliciesUserData(response.result);
		}
	};

	// Check if User follow the user-view Link from Dashboard
	React.useEffect(() => {
		if (location.pathname === '/policies/user-view') {
			dispatch(UserSlice.actions.update({ viewPoliciesAsAdmin: false }));

			getPoliciesUserData();
		} else if (isOrganisationAdmin && location.pathname === '/policies/admin-view') {
			dispatch(UserSlice.actions.update({ viewPoliciesAsAdmin: true }));

			getPoliciesAdminData();
		}
	}, []);

	// Load data based on which view is loaded
	React.useEffect(() => {
		if (isOrganisationAdmin && viewPoliciesAsAdmin) {
			getPoliciesAdminData();
		} else {
			getPoliciesUserData();
		}

		setIsLoading(false);
	}, []);

	// State to manage maximum number of items per section
	const defaultMaxItems: number = 5;

	// FILTER ADMIN VIEW DATA
	const archivedPoliciesAdminData = policiesAdminData.filter((item) => item.status === 'Archived');
	const notArchivedPoliciesAdminData = policiesAdminData.filter((item) => item.status === 'Active');

	const expiredPoliciesAdminData = notArchivedPoliciesAdminData.filter((item) => item.isExpired === true);
	const activePoliciesAdminData = notArchivedPoliciesAdminData.filter((item) => item.isExpired === false);

	const [maxExpiredPolicies, setMaxExpiredPolicies] = React.useState<number>(defaultMaxItems);
	const [maxActivePolicies, setMaxActivePolicies] = React.useState<number>(defaultMaxItems);
	const [maxArchivedPolicies, setMaxArchivedPolicies] = React.useState<number>(defaultMaxItems);

	// FILTER USER VIEW DATA
	const activePoliciesUserData = policiesUserData.filter((item) => item.status === 'Active');
	const archivedPoliciesUserData = policiesUserData.filter((item) => item.status === 'Archived');

	// User Accept Policies logic
	const acceptHandler = async (item?: PolicyUserDashboardAcknowledgement) => {
		setShowAcceptModal(true);

		setPolicyTitle(item?.title || '');

		setPolicyId(item?.id || '');
	};

	// SWITCH ROLE FUNCTION
	const switchRole = () => {
		if (isOrganisationAdmin) {
			if (viewPoliciesAsAdmin) {
				dispatch(UserSlice.actions.update({ viewPoliciesAsAdmin: false }));

				getPoliciesUserData();
			} else {
				dispatch(UserSlice.actions.update({ viewPoliciesAsAdmin: true }));

				getPoliciesAdminData();
			}
		}
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<>
					{isOrganisationAdmin && viewPoliciesAsAdmin ? (
						<SwitchTransition mode="out-in">
							<CSSTransition
								key={`key_${isLoading}`}
								timeout={600}
								classNames="standalone-fade"
								mountOnEnter
								unmountOnExit
							>
								{isLoading ? (
									<LoadingSpinner show={isLoading} />
								) : (
									<>
										<div className="device-info">
											<DeviceIcon />

											<div className="info-text">
												<h2>Switch device</h2>

												<p>
													To provide our users with the best experience, some features of Child<strong>Aware</strong>{' '}
													are only available to use on a <strong>laptop</strong> or similar <strong>desktop</strong>{' '}
													computer.
												</p>
											</div>
										</div>

										<p className="sub-grey-text mbtom-30">
											Your organisation’s child safety policies are listed below. You can upload a new policy, manage
											existing policies, access policy templates and assign policies to users within your organisation.
										</p>

										<div className="general-notification-box">
											<div className="box-left">
												<SwitchUserIcon />

												<div className="info-text">
													<p className="mbtom-8">
														As a <strong>Leader</strong> you can upload and manage your organisation’s policies and
														assign policies to staff, parents, children, volunteers and visitors for review and
														acceptance.
													</p>

													<p>
														You must also review and accept policies <strong>assigned to you</strong> by your
														organisation. Click on ‘View my policies’ to switch roles.
													</p>
												</div>
											</div>

											<Button className="btn-primary small" onClick={switchRole}>
												<SwitchIcon className="link-icon" /> View my policies
											</Button>
										</div>

										{policiesAdminData && policiesAdminData.length > 0 ? (
											<>
												{isOrganisationActive && (
													<div className="submit-btn-container policy-page">
														<Link to="/policy-submit" className="btn-primary submit-btn ">
															Upload new policy
														</Link>
													</div>
												)}

												{expiredPoliciesAdminData && expiredPoliciesAdminData.length > 0 && (
													<section className="info-section">
														<div className="section-header bold">Expired</div>

														{expiredPoliciesAdminData.slice(0, maxExpiredPolicies).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="danger" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.title}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title red">Expired</div>

																					<time className="name red">
																						<DisplayDateExpiry>{item.expiryDate}</DisplayDateExpiry>
																					</time>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Last updated</div>

																					<time className="status">
																						<DisplayDate>{item.updated}</DisplayDate>
																					</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOrganisationActive && (
																				<Link
																					className="btn-text remove d-none d-md-block"
																					to={`/policy-submit/${item.id}`}
																				>
																					Update
																				</Link>
																			)}

																			{item && item.attachmentUrl && (
																				<>
																					<a className="btn-text d-none d-md-block" href={item.attachmentUrl} download>
																						Download
																					</a>

																					<a
																						className="btn-icon mtop-40 d-flex d-md-none"
																						download
																						href={item.attachmentUrl}
																					>
																						<DownloadIcon />
																					</a>
																				</>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}

														{maxExpiredPolicies < expiredPoliciesAdminData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxExpiredPolicies(expiredPoliciesAdminData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {expiredPoliciesAdminData.length - maxExpiredPolicies} more
															</Button>
														)}
													</section>
												)}

												{activePoliciesAdminData && activePoliciesAdminData.length > 0 && (
													<section className="info-section">
														<div className="section-header bold">Active</div>
														{activePoliciesAdminData.slice(0, maxActivePolicies).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="success" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.title}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title green">Expiry</div>

																					<time className="name">
																						<DisplayDateExpiry>{item.expiryDate}</DisplayDateExpiry>
																					</time>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Last updated</div>

																					<time className="status">
																						<DisplayDate>{item.updated}</DisplayDate>
																					</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOrganisationActive && (
																				<Link
																					className="btn-text remove d-none d-md-block"
																					to={`/policy-submit/${item.id}`}
																				>
																					Update
																				</Link>
																			)}

																			{item && item.attachmentUrl && (
																				<>
																					<a className="btn-text d-none d-md-block" href={item.attachmentUrl} download>
																						Download
																					</a>

																					<a
																						className="btn-icon mtop-40 d-flex d-md-none"
																						download
																						href={item.attachmentUrl}
																					>
																						<DownloadIcon />
																					</a>
																				</>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}

														{maxActivePolicies < activePoliciesAdminData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxActivePolicies(activePoliciesAdminData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {activePoliciesAdminData.length - maxActivePolicies} more
															</Button>
														)}
													</section>
												)}

												{archivedPoliciesAdminData && archivedPoliciesAdminData.length > 0 && (
													<section className="info-section">
														<div className="section-header bold">Archived</div>
														{archivedPoliciesAdminData.slice(0, maxArchivedPolicies).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="archived" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.title}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title green">Expired</div>

																					<time className="name">
																						<DisplayDateExpiry>{item.expiryDate}</DisplayDateExpiry>
																					</time>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Last updated</div>

																					<time className="status">
																						<DisplayDate>{item.updated}</DisplayDate>
																					</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{isOrganisationActive && (
																				<Link
																					className="btn-text remove d-none d-md-block"
																					to={`/policy-submit/${item.id}`}
																				>
																					Update
																				</Link>
																			)}

																			{item && item.attachmentUrl && (
																				<>
																					<a className="btn-text d-none d-md-block" href={item.attachmentUrl} download>
																						Download
																					</a>

																					<a
																						className="btn-icon mtop-40 d-flex d-md-none"
																						download
																						href={item.attachmentUrl}
																					>
																						<DownloadIcon />
																					</a>
																				</>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}

														{maxArchivedPolicies < archivedPoliciesAdminData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxArchivedPolicies(archivedPoliciesAdminData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {archivedPoliciesAdminData.length - maxArchivedPolicies} more
															</Button>
														)}
													</section>
												)}
											</>
										) : (
											<div className="empty-notification">
												<h2 className="info-text">
													You have not yet submitted any <strong>Policies</strong>.
												</h2>

												{isOrganisationActive && (
													<Link to="/policy-submit" className="btn-primary submit-btn">
														Submit new policy
													</Link>
												)}
											</div>
										)}

										{isOrganisationActive && (
											<section className="general-guide">
												<h2 className="guide-header">Policy templates</h2>

												<p className="guide-info">
													If you need assistance with policies for your organisation, review our helpful policy
													templates.
												</p>

												<Link className="btn btn-primary guide-btn" to="/policy-templates">
													View Policy templates
												</Link>
											</section>
										)}

										<ScrollToTopButton extraClass="mtop-30" />
									</>
								)}
							</CSSTransition>
						</SwitchTransition>
					) : (
						<SwitchTransition mode="out-in">
							<CSSTransition
								key={`key_${isLoading}`}
								timeout={600}
								classNames="standalone-fade"
								mountOnEnter
								unmountOnExit
							>
								{isLoading ? (
									<LoadingSpinner show={isLoading} />
								) : (
									<>
										<p className="sub-grey-text mbtom-30">
											Your organisation has assigned the following child safety policies to you for your review and
											acceptance.
										</p>

										{isOrganisationAdmin && (
											<div className="general-notification-box admin-view">
												<div className="box-left">
													<SwitchUserIcon />

													<div className="info-text">
														<p className="mbtom-8">
															Below are the policies <strong>assigned to you</strong> by your organisation to review and
															accept.
														</p>

														<p>
															As a <strong>Leader</strong>, you can also manage your organisation’s policies. Click on
															‘Manage policies’ to switch roles.
														</p>
													</div>
												</div>

												<Button className="btn-primary small" onClick={switchRole}>
													<SwitchIcon className="link-icon" /> Manage policies
												</Button>
											</div>
										)}

										{policiesUserData && policiesUserData.length > 0 ? (
											<>
												{activePoliciesUserData && activePoliciesUserData.length > 0 && (
													<section className="info-section">
														<div className="section-header bold">To review and accept</div>
														{activePoliciesUserData.map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType={item.isAcknowledged === true ? 'success' : 'danger'} />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-8 detail-name">{item.title}</div>

																				{item.isAcknowledged && item.acknowledged && (
																					<div className="col-lg-4 detail-status">
																						<div className="title">Accepted</div>

																						<time className="status">
																							<DisplayDate>{item.acknowledged}</DisplayDate>
																						</time>
																					</div>
																				)}
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{item.isAcknowledged ? (
																				<>
																					{item.attachmentUrl && (
																						<a className="btn-text" href={item.attachmentUrl} download>
																							Download
																						</a>
																					)}
																				</>
																			) : (
																				<>
																					{item.attachmentUrl && (
																						<a className="btn-text remove" href={item.attachmentUrl} download>
																							Download
																						</a>
																					)}

																					{isOrganisationActive && (
																						<Button className="btn-text" onClick={() => acceptHandler(item)}>
																							Accept
																						</Button>
																					)}
																				</>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
													</section>
												)}

												{archivedPoliciesUserData && archivedPoliciesUserData.length > 0 && (
													<section className="info-section">
														<div className="section-header bold">Available to view</div>
														{archivedPoliciesUserData.map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="read" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-12 detail-name">{item.title}</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{item && item.attachmentUrl && (
																				<a className="btn-text" href={item.attachmentUrl} download>
																					Download
																				</a>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}
													</section>
												)}

												<GlobalModal isOpen={showAcceptModal} setIsOpen={setShowAcceptModal} titleText="Accept policy">
													<AcceptModal
														title={policyTitle}
														policyId={policyId}
														getPoliciesUserData={getPoliciesUserData}
													/>
												</GlobalModal>
											</>
										) : (
											<NoInfoMessage message="You do not have any policies assigned or available to view." />
										)}
									</>
								)}
							</CSSTransition>
						</SwitchTransition>
					)}
				</>
			)}
		</div>
	);
};

export default Policies;
